import * as B from '@effect/data/Boolean';
import * as Duration from '@effect/data/Duration';
import { AdminBar, Head, OnboardingWorkflow, PageMetaData } from '@infl-ts/ui-advocate-components';
import type { SiteData } from '@infl-ts/ui-advocate-setup';
import { ConsentWorkflow, fetchConfigs, FetchSiteConfig, Page } from '@infl-ts/ui-advocate-setup';
import { GoogleAnalytics } from '@infl-ts/ui-google-analytics';
import { hasMode, Modes } from '@infl-ts/util-app-config';
import { DatadogSessionSettings } from '@infl-ts/util-datadog';
import { DE$ } from '@infl-ts/util-datum';
import { extractCookieValue } from '@infl-ts/util-extract-cookie';
import { O$ } from '@infl-ts/util-fp-ts';
import { i18nInstance } from '@infl-ts/util-i18n-provider';
import type { QueryError } from '@infl-ts/util-make-apollo-decoded';
import { queryResultToDE } from '@infl-ts/util-react-query';
import { ScrollRestoration, ScrollToTop } from '@infl-ts/util-scroll';
import { UnsavedChanges } from '@infl-ts/util-use-unsaved-changes';
import * as DE from '@nll/datum/lib/DatumEither';
import type { DehydratedState } from '@tanstack/react-query';
import { HydrationBoundary, useQuery } from '@tanstack/react-query';
import { constFalse, constNull, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import type { Resource } from 'i18next';
import type { NextPageContext } from 'next';

import { FACEBOOK_APP_ID } from '../env';
import { LeafBlocks } from '../lib/leaf-blocks';
import { prefetchAppData } from '../lib/prefetch-app-data';
import UrlTransformer from '../lib/url-transformer';

const MetaData = () =>
  pipe(
    useQuery({
      queryKey: ['siteConfigData'],
      queryFn: ({ meta }) => fetchConfigs(meta ?? {}),
      staleTime: Duration.toMillis(Duration.hours(1)),
      gcTime: Duration.toMillis(Duration.hours(1.5))
    }),
    queryResultToDE,
    DE.map(({ configs: { appConfig } }) => appConfig),
    DE.chain(DE.fromOption(constNull)),
    DE.map(({ programName, hubLogoImageURL, signInWelcomeText }) => (
      <PageMetaData
        key={programName}
        siteName={programName ?? undefined}
        title={programName ?? ''}
        imgUrl={hubLogoImageURL ?? ''}
        description={signInWelcomeText ?? ''}
        fbId={FACEBOOK_APP_ID}
      />
    )),
    DE$.getSuccessOrElse(null)
  );

const FavIcon = ({ siteConfig }: { siteConfig: DE.DatumEither<QueryError, SiteData> }) =>
  pipe(
    siteConfig,
    DE.map(siteConfig => siteConfig.favIcon),
    DE$.getSuccessOrElse(null),
    favIcon =>
      favIcon ? (
        <Head>
          <link rel="shortcut icon" href={favIcon} />
        </Head>
      ) : null
  );

const ProgramName = ({ siteConfig }: { siteConfig: DE.DatumEither<QueryError, SiteData> }) =>
  pipe(
    siteConfig,
    DE.map(siteConfig => siteConfig.configs.appConfig),
    DE$.getSuccessOrElse(O.none),
    O$.unify,
    O.match(constNull, ({ programName }) => (
      <Head key="site-title">
        <title>{programName}</title>
      </Head>
    ))
  );

const Home = (props: {
  dehydratedState: DehydratedState;
  defaultLocales: Resource;
  cookieLang?: string;
}) => (
  <HydrationBoundary state={props.dehydratedState}>
    <MetaData />
    <FetchSiteConfig>
      {siteConfig => (
        <>
          <UrlTransformer />
          <GoogleAnalytics
            analyticsConfig={pipe(
              siteConfig,
              DE.map(c => c.configs.analyticsConfig)
            )}
          />
          <>
            <ProgramName siteConfig={siteConfig} />
            <FavIcon siteConfig={siteConfig} />
          </>
          <Page
            siteConfig={siteConfig}
            leafBlocks={LeafBlocks}
            defaultLocales={props.defaultLocales}
            cookieLang={props.cookieLang}>
            {renderedBlocks => (
              <UnsavedChanges>
                <ScrollRestoration whiteList={['/rewards', '/']} />
                <ScrollToTop />
                <DatadogSessionSettings
                  isPagePublic={pipe(
                    siteConfig,
                    DE.map(({ configs: { appConfig } }) => appConfig),
                    DE.map(O.map(hasMode(Modes.map.public))),
                    DE.chain(DE.fromOption(constFalse)),
                    DE$.getSuccessOrElse(false)
                  )}
                />
                <ConsentWorkflow
                  config={pipe(
                    siteConfig,
                    DE.map(
                      ({ hubName, consentRequired, configs: { appConfig, consentConfig } }) => ({
                        hubName,
                        consentConfig,
                        isPublicPage: pipe(
                          appConfig,
                          O.map(hasMode(Modes.map.public)),
                          O.map(B.or(!consentRequired))
                        )
                      })
                    )
                  )}>
                  <OnboardingWorkflow siteConfig={siteConfig}>
                    {/* TODO(ADVO2-1944): this component should be in _app.tsx,
                            since it should show up above any page */}
                    <AdminBar />
                    {renderedBlocks}
                  </OnboardingWorkflow>
                </ConsentWorkflow>
              </UnsavedChanges>
            )}
          </Page>
        </>
      )}
    </FetchSiteConfig>
  </HydrationBoundary>
);

// Currently cannot transition to using getServerSideProps due to: https://github.com/vercel/next.js/discussions/28183
Home.getInitialProps = async (ctx: NextPageContext) => {
  if (typeof window !== 'undefined') return {};

  const dehydratedState = await prefetchAppData(ctx);

  const cookieLang = extractCookieValue(ctx.req?.headers.cookie, 'lang');

  const i18n = i18nInstance(cookieLang || 'en');

  const defaultLocales = i18n.store.data || {};

  return {
    dehydratedState,
    cookieLang,
    defaultLocales
  };
};

export default Home;
